/**
 * Composant QuoteDialog - Dialogue de génération de devis
 * Ce composant gère la création de devis avec :
 * - Blocs de compétences avec heures
 * - FOAD, Test de sélection, Mise à niveau
 * - Modules additionnels
 * - Durée totale et répartition multi-payeurs
 */

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Download, Plus, X, Clock, GraduationCap } from 'lucide-react';
import { TypeFinancementPicker } from '../../financement/TypeFinancementPicker';

// Fonction utilitaire pour calculer le total global
const calculateTotalGlobal = (quoteData, config) => {
  const heuresBCs = Object.values(quoteData.bc_heures || {}).reduce((sum, h) => sum + (parseInt(h) || 0), 0);
  const heuresFoad = quoteData.foad_actif ? (parseInt(quoteData.nb_heures_foad) || 0) : 0;
  const taux = parseFloat(quoteData.taux_horaire) || config.taux?.[quoteData.taux_type] || 11;
  const heuresTotal = (heuresBCs + heuresFoad) * taux;
  const modulesTotal = quoteData.modules_additionnels?.reduce((sum, m) => sum + (m.montant || 0), 0) || 0;
  const testSelection = quoteData.test_selection ? (quoteData.test_selection_prix || 0) : 0;
  const journeesTotal = (quoteData.journee_mise_niveau || 0) * (quoteData.journee_mise_niveau_prix || 0);
  return heuresTotal + modulesTotal + testSelection + journeesTotal;
};

// Composant pour les blocs de compétences
const BlocCompetenceItem = ({ bc, quoteData, setQuoteData }) => {
  const isChecked = quoteData.bc_codes.includes(bc.code);
  
  const handleCheck = (checked) => {
    const codes = quoteData.bc_codes.split(',').filter(c => c);
    if (checked) {
      const defaultHeure = quoteData._defaultHours?.[bc.code] || 0;
      setQuoteData({
        ...quoteData, 
        bc_codes: [...codes, bc.code].join(','),
        bc_heures: {...(quoteData.bc_heures || {}), [bc.code]: defaultHeure}
      });
    } else {
      const newHeures = {...(quoteData.bc_heures || {})};
      delete newHeures[bc.code];
      setQuoteData({
        ...quoteData, 
        bc_codes: codes.filter(c => c !== bc.code).join(','),
        bc_heures: newHeures
      });
    }
  };

  return (
    <div className="p-3 bg-white rounded-lg border space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`quote-${bc.code}`}
            checked={isChecked}
            onCheckedChange={handleCheck}
          />
          <Label htmlFor={`quote-${bc.code}`} className="text-sm font-medium">
            {bc.code}
          </Label>
        </div>
        {isChecked && (
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Heures:</Label>
            <Input
              type="number"
              value={quoteData.bc_heures?.[bc.code] || ''}
              onChange={(e) => setQuoteData({
                ...quoteData,
                bc_heures: {...(quoteData.bc_heures || {}), [bc.code]: parseInt(e.target.value) || 0}
              })}
              placeholder="0"
              className="w-20 h-8 text-sm"
              data-testid={`input-heures-${bc.code}`}
            />
          </div>
        )}
      </div>
      {isChecked && (
        <Input
          value={quoteData.bc_intitules?.[bc.code] || bc.nom}
          onChange={(e) => setQuoteData({
            ...quoteData,
            bc_intitules: {...(quoteData.bc_intitules || {}), [bc.code]: e.target.value}
          })}
          className="text-sm"
          placeholder={`Intitulé personnalisé pour ${bc.code}`}
        />
      )}
    </div>
  );
};

// Composant pour la section facturation d'un payeur
const FacturationSection = ({ 
  id, 
  label, 
  payeurKey, 
  quoteData, 
  setQuoteData, 
  config, 
  selectedProspect,
  showAddressFields = true,
  buttonColor = 'blue'
}) => {
  const facturation = quoteData[payeurKey] || {};
  const isActive = facturation.actif;
  
  const handleActivate = (checked) => {
    if (checked) {
      const totalGlobal = calculateTotalGlobal(quoteData, config);
      const montantStagiaire = payeurKey !== 'facturation_stagiaire' && quoteData.facturation_stagiaire?.actif ? (quoteData.facturation_stagiaire?.montant || 0) : 0;
      const montantEntreprise = payeurKey !== 'facturation_entreprise' && quoteData.facturation_entreprise?.actif ? (quoteData.facturation_entreprise?.montant || 0) : 0;
      const montantFinanceur = payeurKey !== 'facturation_financeur' && quoteData.facturation_financeur?.actif ? (quoteData.facturation_financeur?.montant || 0) : 0;
      const solde = Math.max(0, totalGlobal - montantStagiaire - montantEntreprise - montantFinanceur);
      
      setQuoteData({
        ...quoteData,
        [payeurKey]: {...facturation, actif: true, montant: solde}
      });
    } else {
      setQuoteData({
        ...quoteData,
        [payeurKey]: {...facturation, actif: false}
      });
    }
  };

  const handleSoldeClick = () => {
    const totalGlobal = calculateTotalGlobal(quoteData, config);
    const montantStagiaire = payeurKey !== 'facturation_stagiaire' && quoteData.facturation_stagiaire?.actif ? (quoteData.facturation_stagiaire?.montant || 0) : 0;
    const montantEntreprise = payeurKey !== 'facturation_entreprise' && quoteData.facturation_entreprise?.actif ? (quoteData.facturation_entreprise?.montant || 0) : 0;
    const montantFinanceur = payeurKey !== 'facturation_financeur' && quoteData.facturation_financeur?.actif ? (quoteData.facturation_financeur?.montant || 0) : 0;
    const solde = Math.max(0, totalGlobal - montantStagiaire - montantEntreprise - montantFinanceur);
    
    setQuoteData({
      ...quoteData,
      [payeurKey]: {...facturation, montant: solde}
    });
  };

  const buttonColorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100',
    purple: 'bg-purple-50 border-purple-200 text-purple-600 hover:bg-purple-100',
    green: 'bg-green-50 border-green-200 text-green-600 hover:bg-green-100'
  };

  return (
    <div className="p-3 bg-white rounded border space-y-3">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id={id}
            checked={isActive}
            onCheckedChange={handleActivate}
          />
          <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
        </div>
        {isActive && (
          <div className="flex items-center gap-2 flex-1">
            {showAddressFields && (
              <Input
                value={facturation.nom || ''}
                onChange={(e) => setQuoteData({
                  ...quoteData,
                  [payeurKey]: {...facturation, nom: e.target.value}
                })}
                placeholder={`Nom du ${label.toLowerCase()} *`}
                className="flex-1 min-w-[160px]"
                data-testid={`input-${payeurKey}-nom`}
              />
            )}
            <Label className="text-sm text-slate-600">Montant:</Label>
            <Input
              type="number"
              value={facturation.montant || ''}
              onChange={(e) => setQuoteData({
                ...quoteData,
                [payeurKey]: {...facturation, montant: parseFloat(e.target.value) || 0}
              })}
              placeholder="0"
              className="w-28"
              data-testid={`input-${payeurKey}-montant`}
            />
            <span className="text-sm text-slate-500">€</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`text-xs ${buttonColorClasses[buttonColor]}`}
              onClick={handleSoldeClick}
            >
              {payeurKey === 'facturation_stagiaire' ? '100%' : 'Solde'}
            </Button>
          </div>
        )}
      </div>
      {isActive && showAddressFields && (
        <div className="ml-6 space-y-2">
          {payeurKey === 'facturation_financeur' && (
            <TypeFinancementPicker
              value={facturation.type_financement}
              onChange={(t) => setQuoteData({ ...quoteData, [payeurKey]: { ...facturation, type_financement: t } })}
              testId="quote-type-financement"
            />
          )}
          <Input
            value={facturation.adresse || ''}
            onChange={(e) => setQuoteData({
              ...quoteData,
              [payeurKey]: {...facturation, adresse: e.target.value}
            })}
            placeholder="Adresse *"
            className="w-full"
            data-testid={`input-${payeurKey}-adresse`}
          />
          <div className="flex gap-2">
            <Input
              value={facturation.code_postal || ''}
              onChange={(e) => setQuoteData({
                ...quoteData,
                [payeurKey]: {...facturation, code_postal: e.target.value}
              })}
              placeholder="Code postal *"
              className="w-32"
              data-testid={`input-${payeurKey}-cp`}
            />
            <Input
              value={facturation.ville || ''}
              onChange={(e) => setQuoteData({
                ...quoteData,
                [payeurKey]: {...facturation, ville: e.target.value}
              })}
              placeholder="Ville *"
              className="flex-1"
              data-testid={`input-${payeurKey}-ville`}
            />
          </div>
        </div>
      )}
      {isActive && !showAddressFields && selectedProspect && (
        <div className="ml-6 text-xs text-slate-500">
          Adresse: {selectedProspect.adresse || '-'}, {selectedProspect.code_postal || ''} {selectedProspect.ville || ''}
        </div>
      )}
    </div>
  );
};

export const QuoteDialog = ({
  open,
  onOpenChange,
  selectedProspect,
  quoteData,
  setQuoteData,
  config,
  onGenerateQuote
}) => {
  const totalHeuresBCs = Object.values(quoteData.bc_heures || {}).reduce((sum, h) => sum + (parseInt(h) || 0), 0);
  const totalGlobal = calculateTotalGlobal(quoteData, config);
  
  const montantStagiaire = quoteData.facturation_stagiaire?.actif ? (quoteData.facturation_stagiaire?.montant || 0) : 0;
  const montantEntreprise = quoteData.facturation_entreprise?.actif ? (quoteData.facturation_entreprise?.montant || 0) : 0;
  const montantFinanceur = quoteData.facturation_financeur?.actif ? (quoteData.facturation_financeur?.montant || 0) : 0;
  const totalReparti = montantStagiaire + montantEntreprise + montantFinanceur;
  const resteARepartir = totalGlobal - totalReparti;

  const heuresFoad = quoteData.foad_actif ? (parseInt(quoteData.nb_heures_foad) || 0) : 0;
  const taux = parseFloat(quoteData.taux_horaire) || config.taux?.[quoteData.taux_type] || 11;

  // Calculer la durée totale (centre + entreprise)
  const dureeTotale = (parseInt(quoteData.duree_centre) || 0) + (parseInt(quoteData.duree_entreprise) || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle>Générer un devis</DialogTitle>
          <DialogDescription>
            Pour: {selectedProspect?.prenom} {selectedProspect?.nom}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_debut_devis">Date de début</Label>
              <Input
                id="date_debut_devis"
                type="date"
                value={quoteData.date_debut}
                onChange={(e) => setQuoteData({...quoteData, date_debut: e.target.value})}
                data-testid="input-quote-date-debut"
              />
            </div>
            <div>
              <Label htmlFor="date_fin_devis">Date de fin</Label>
              <Input
                id="date_fin_devis"
                type="date"
                value={quoteData.date_fin}
                onChange={(e) => setQuoteData({...quoteData, date_fin: e.target.value})}
                data-testid="input-quote-date-fin"
              />
            </div>
          </div>

          {/* Section principale - Blocs de compétences et options */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
            <div>
              <Label className="text-green-800 font-medium flex items-center gap-2">
                <GraduationCap className="h-4 w-4" />
                Formation - Blocs de compétences
              </Label>
              <p className="text-xs text-green-600 mb-3">Cochez les blocs et saisissez les heures pour chacun</p>
              {quoteData._depuisPif && (
                <p className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3" data-testid="devis-heures-depuis-pif">
                  Heures reprises du PIF du candidat, allégements déduits ({quoteData._depuisPif.allegements} h en moins).
                </p>
              )}
            </div>
            
            {/* Blocs de compétences */}
            <div className="space-y-3">
              {config.blocs_competences.filter(bc => bc.code !== 'BC4').map(bc => (
                <BlocCompetenceItem 
                  key={bc.code} 
                  bc={bc} 
                  quoteData={quoteData} 
                  setQuoteData={setQuoteData} 
                />
              ))}
            </div>
            
            {/* FOAD - intégré dans le bloc */}
            <div className="p-3 bg-white rounded-lg border space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="foad_actif"
                    checked={quoteData.foad_actif || false}
                    onCheckedChange={(checked) => setQuoteData({
                      ...quoteData, 
                      foad_actif: checked,
                      nb_heures_foad: checked ? (quoteData.nb_heures_foad || quoteData._defaultFoad || 0) : quoteData.nb_heures_foad
                    })}
                    data-testid="checkbox-foad"
                  />
                  <Label htmlFor="foad_actif" className="text-sm font-medium">FOAD</Label>
                  <span className="text-xs text-slate-400">(Formation à distance)</span>
                </div>
                {quoteData.foad_actif && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Heures:</Label>
                    <Input
                      type="number"
                      value={quoteData.nb_heures_foad || ''}
                      onChange={(e) => setQuoteData({...quoteData, nb_heures_foad: parseInt(e.target.value) || 0})}
                      placeholder="0"
                      className="w-20 h-8 text-sm"
                      data-testid="input-quote-heures-foad"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Test de sélection */}
            <div className="p-3 bg-white rounded-lg border space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="test_selection"
                    checked={quoteData.test_selection || false}
                    onCheckedChange={(checked) => setQuoteData({...quoteData, test_selection: checked})}
                    data-testid="checkbox-test-selection"
                  />
                  <Label htmlFor="test_selection" className="text-sm font-medium">Test de sélection</Label>
                </div>
                {quoteData.test_selection && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Prix:</Label>
                    <Input
                      type="number"
                      value={quoteData.test_selection_prix || ''}
                      onChange={(e) => setQuoteData({...quoteData, test_selection_prix: parseFloat(e.target.value) || 0})}
                      placeholder="0"
                      className="w-24 h-8 text-sm"
                      data-testid="input-test-selection-prix"
                    />
                    <span className="text-xs text-slate-500">€</span>
                  </div>
                )}
              </div>
            </div>

            {/* Journées de mise à niveau */}
            <div className="p-3 bg-white rounded-lg border space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-sm font-medium">Journées mise à niveau VNM</Label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Nb jours:</Label>
                    <Input
                      type="number"
                      min="0"
                      value={quoteData.journee_mise_niveau || ''}
                      onChange={(e) => setQuoteData({...quoteData, journee_mise_niveau: parseInt(e.target.value) || 0})}
                      placeholder="0"
                      className="w-16 h-8 text-sm"
                      data-testid="input-journees-niveau"
                    />
                  </div>
                  {(quoteData.journee_mise_niveau > 0) && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500">Prix/jour:</Label>
                      <Input
                        type="number"
                        value={quoteData.journee_mise_niveau_prix || ''}
                        onChange={(e) => setQuoteData({...quoteData, journee_mise_niveau_prix: parseFloat(e.target.value) || 0})}
                        placeholder="0"
                        className="w-20 h-8 text-sm"
                        data-testid="input-prix-journee-niveau"
                      />
                      <span className="text-xs text-slate-500">€</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modules additionnels */}
            <div className="p-3 bg-white rounded-lg border space-y-2">
              <Label className="text-sm font-medium">Modules additionnels</Label>
              <div className="space-y-2">
                {quoteData.modules_additionnels?.map((mod, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Input
                      value={mod.intitule}
                      onChange={(e) => {
                        const updated = [...quoteData.modules_additionnels];
                        updated[idx].intitule = e.target.value;
                        setQuoteData({...quoteData, modules_additionnels: updated});
                      }}
                      placeholder="Intitulé du module"
                      className="flex-1 h-8 text-sm"
                    />
                    <Input
                      type="number"
                      value={mod.montant || ''}
                      onChange={(e) => {
                        const updated = [...quoteData.modules_additionnels];
                        updated[idx].montant = parseFloat(e.target.value) || 0;
                        setQuoteData({...quoteData, modules_additionnels: updated});
                      }}
                      placeholder="€"
                      className="w-20 h-8 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = quoteData.modules_additionnels.filter((_, i) => i !== idx);
                        setQuoteData({...quoteData, modules_additionnels: updated});
                      }}
                      className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                
                <div className="flex items-center gap-2">
                  <Input
                    value={quoteData.nouveau_module || ''}
                    onChange={(e) => setQuoteData({...quoteData, nouveau_module: e.target.value})}
                    placeholder="Nouveau module additionnel..."
                    className="flex-1 h-8 text-sm"
                    data-testid="input-nouveau-module"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (quoteData.nouveau_module?.trim()) {
                        setQuoteData({
                          ...quoteData,
                          modules_additionnels: [...(quoteData.modules_additionnels || []), { intitule: quoteData.nouveau_module.trim(), montant: 0 }],
                          nouveau_module: ''
                        });
                      }
                    }}
                    className="bg-[#8DC63F] text-white hover:bg-[#7AB62F] h-8"
                    data-testid="btn-add-module"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Récapitulatif heures BCs + FOAD */}
            <div className="pt-3 border-t border-green-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-green-800">Total heures en centre (BCs):</span>
                <span className="font-bold text-green-700">{totalHeuresBCs} h</span>
              </div>
              {quoteData.foad_actif && heuresFoad > 0 && (
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-green-800">Heures FOAD:</span>
                  <span className="font-bold text-green-700">{heuresFoad} h</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-green-200">
                <span className="text-green-800 font-medium">Total heures formation:</span>
                <span className="text-lg font-bold text-green-700">{totalHeuresBCs + heuresFoad} h</span>
              </div>
            </div>
          </div>

          {/* Durée totale et Taux */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4" />
              Durée totale de la formation
            </Label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="duree_centre" className="text-xs text-slate-500">En centre (h)</Label>
                <Input
                  id="duree_centre"
                  type="number"
                  value={quoteData.duree_centre || ''}
                  onChange={(e) => setQuoteData({...quoteData, duree_centre: parseInt(e.target.value) || 0})}
                  placeholder="600"
                  className="h-9"
                  data-testid="input-duree-centre"
                />
              </div>
              <div>
                <Label htmlFor="duree_entreprise" className="text-xs text-slate-500">En entreprise (h)</Label>
                <Input
                  id="duree_entreprise"
                  type="number"
                  value={quoteData.duree_entreprise || ''}
                  onChange={(e) => setQuoteData({...quoteData, duree_entreprise: parseInt(e.target.value) || 0})}
                  placeholder="450"
                  className="h-9"
                  data-testid="input-duree-entreprise"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Durée totale</Label>
                <div className="h-9 flex items-center px-3 bg-white border rounded-md text-sm font-medium text-slate-700">
                  {dureeTotale} h
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="taux_horaire" className="text-xs text-slate-500">Taux horaire (€)</Label>
                  <Input
                    id="taux_horaire"
                    type="number"
                    step="0.01"
                    value={quoteData.taux_horaire || ''}
                    onChange={(e) => setQuoteData({...quoteData, taux_horaire: parseFloat(e.target.value) || 0})}
                    placeholder={`Défaut: ${config.taux?.[quoteData.taux_type] || 11}€`}
                    className="h-9"
                    data-testid="input-quote-taux"
                  />
                </div>
                <div className="flex flex-col gap-1 pt-4">
                  {[['Formation', quoteData._tauxFormation, 'formation'], ['Apprentissage', quoteData._tauxApprentissage, 'apprentissage']].map(([label, valeur, id]) => (
                    <Button
                      key={id}
                      type="button"
                      size="sm"
                      variant={parseFloat(quoteData.taux_horaire) === valeur ? 'default' : 'outline'}
                      className="h-7 text-xs"
                      disabled={!valeur}
                      onClick={() => setQuoteData({...quoteData, taux_horaire: valeur})}
                      title={valeur ? '' : 'Taux non renseigné dans le profil du centre'}
                      data-testid={`btn-taux-${id}`}
                    >
                      {label} {valeur ? `(${valeur} €)` : '(non renseigné)'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Répartition de la facturation */}
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800">Répartition de la facturation</h4>
            <p className="text-xs text-blue-600">Un seul devis est généré : s'il y a plusieurs payeurs, la répartition (financeurs : CPF, France Travail, Région, OPCO… / structure / reste à charge stagiaire) figure sur le document. Une facture sera ensuite faite par payeur.</p>
            
            {/* Total à répartir */}
            <div className="p-3 bg-white rounded border mb-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-700">Total à répartir:</span>
                <span className="text-lg font-bold text-blue-700">{totalGlobal.toLocaleString('fr-FR')} €</span>
              </div>
              {totalReparti > 0 && (
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-slate-500">Déjà réparti:</span>
                  <span className="text-sm text-slate-600">{totalReparti.toLocaleString('fr-FR')} €</span>
                </div>
              )}
              {resteARepartir !== 0 && totalReparti > 0 && (
                <div className={`flex justify-between items-center mt-1 ${resteARepartir < 0 ? 'text-red-600' : 'text-orange-600'}`}>
                  <span className="text-xs">{resteARepartir > 0 ? 'Reste à répartir:' : 'Dépassement:'}</span>
                  <span className="text-sm font-medium">{Math.abs(resteARepartir).toLocaleString('fr-FR')} €</span>
                </div>
              )}
            </div>
            
            {/* Sections facturation */}
            <FacturationSection
              id="fact_stagiaire"
              label="Stagiaire"
              payeurKey="facturation_stagiaire"
              quoteData={quoteData}
              setQuoteData={setQuoteData}
              config={config}
              selectedProspect={selectedProspect}
              showAddressFields={false}
              buttonColor="green"
            />
            
            <FacturationSection
              id="fact_entreprise"
              label="Entreprise tiers"
              payeurKey="facturation_entreprise"
              quoteData={quoteData}
              setQuoteData={setQuoteData}
              config={config}
              selectedProspect={selectedProspect}
              buttonColor="blue"
            />
            
            <FacturationSection
              id="fact_financeur"
              label="Financeur"
              payeurKey="facturation_financeur"
              quoteData={quoteData}
              setQuoteData={setQuoteData}
              config={config}
              selectedProspect={selectedProspect}
              buttonColor="purple"
            />
          </div>
          
          {/* Estimation total */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-700">Estimation du total:</p>
            <p className="text-2xl font-bold text-[#8DC63F]">
              {totalGlobal.toLocaleString('fr-FR')} €
            </p>
            <p className="text-xs text-slate-500 mt-1">
              ({totalHeuresBCs}h BCs{quoteData.foad_actif && heuresFoad > 0 ? ` + ${heuresFoad}h FOAD` : ''}) × {taux}€/h
              {quoteData.modules_additionnels?.length > 0 && ` + ${quoteData.modules_additionnels.length} module(s)`}
              {quoteData.test_selection && ` + Test sélection`}
              {quoteData.journee_mise_niveau > 0 && ` + ${quoteData.journee_mise_niveau} jour(s) mise à niveau`}
            </p>
            {(quoteData.facturation_stagiaire?.actif || quoteData.facturation_entreprise?.actif || quoteData.facturation_financeur?.actif) && (
              <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600">
                <span className="font-medium">Répartition:</span>
                {quoteData.facturation_stagiaire?.actif && quoteData.facturation_stagiaire?.montant > 0 && (
                  <span className="ml-2">Stagiaire: {quoteData.facturation_stagiaire.montant.toLocaleString('fr-FR')}€</span>
                )}
                {quoteData.facturation_entreprise?.actif && quoteData.facturation_entreprise?.montant > 0 && (
                  <span className="ml-2">| {quoteData.facturation_entreprise.nom || 'Entreprise'}: {quoteData.facturation_entreprise.montant.toLocaleString('fr-FR')}€</span>
                )}
                {quoteData.facturation_financeur?.actif && quoteData.facturation_financeur?.montant > 0 && (
                  <span className="ml-2">| {quoteData.facturation_financeur.nom || 'Financeur'}: {quoteData.facturation_financeur.montant.toLocaleString('fr-FR')}€</span>
                )}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={onGenerateQuote}
            className="bg-[#8DC63F] hover:bg-[#7AB62F]"
            data-testid="generate-quote-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Générer PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
